import React, { useRef, useCallback, useState, useEffect } from 'react';
import { ScrollArea } from './ui/scroll-area';
import {
  Reply, ReplyAll, Forward, Star, Trash2, Archive,
  Paperclip, Download, ArrowLeft, ChevronLeft, ChevronRight,
  AlertOctagon, X
} from 'lucide-react';
import { cn, formatFullDate, formatFileSize, getInitials } from '../lib/utils';
import { formatFromDisplay, fromDisplayName, primaryFromEmail } from '../lib/formatFrom';
import {
  extractEmailDomain,
  isOwnSenderDomain,
  senderFaviconUrl,
  senderAvatarColor,
} from '../lib/senderAvatar';
import { sanitizeHtml } from '../lib/sanitizeHtml';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Affiche le HTML d'un email dans un iframe isolé pour ne pas hériter du thème sombre
const EmailBodyIframe = ({ html }) => {
  const ref = useRef(null);
  const [height, setHeight] = useState(300);

  useEffect(() => {
    const handler = (e) => {
      if (e.source === ref.current?.contentWindow && e.data?.type === 'email-height' && typeof e.data.height === 'number') {
        setHeight(e.data.height);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const rawHtml = html || '';
  const cleanHtml = sanitizeHtml(rawHtml)
    .replace(/<a(\s)/gi, '<a target="_blank" rel="noopener noreferrer"$1');

  const srcDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><style>
    * { box-sizing: border-box; }
    body { margin: 0; padding: 12px; font-family: Arial, sans-serif; font-size: 14px; line-height: 1.6; background: #fff; color: #222; overflow-x: hidden; }
    #mail-wrapper { width: 100%; overflow: hidden; transform-origin: top left; }
    img { max-width: 100% !important; height: auto !important; }
    img[src=""], img:not([src]) { display: none; }
    a { color: #1a73e8; }
    pre { white-space: pre-wrap; word-break: break-word; }
  </style></head><body><div id="mail-wrapper">${cleanHtml}</div><script>
    function fit() {
      var w = document.getElementById('mail-wrapper');
      if (!w) return;
      var contentW = w.scrollWidth;
      var viewW = document.documentElement.clientWidth - 24;
      if (contentW > viewW && viewW > 0) {
        var scale = viewW / contentW;
        w.style.transform = 'scale(' + scale + ')';
        w.style.transformOrigin = 'top left';
        w.style.width = contentW + 'px';
        document.body.style.height = (w.scrollHeight * scale + 32) + 'px';
        document.body.style.overflow = 'hidden';
      } else {
        w.style.transform = '';
        w.style.width = '';
      }
      var h = Math.max(w.scrollHeight * (parseFloat(w.style.transform ? w.style.transform.replace('scale(','').replace(')','') : '1') || 1), document.body.scrollHeight);
      window.parent.postMessage({type:'email-height', height: Math.ceil(h) + 32}, '*');
    }
    fit();
    window.addEventListener('load', fit);
    window.addEventListener('resize', fit);
    new MutationObserver(fit).observe(document.body, {childList:true, subtree:true, attributes:true});
    setTimeout(fit, 500);
    setTimeout(fit, 1500);
  <\/script></body></html>`;

  return <iframe ref={ref} srcDoc={srcDoc} style={{ width: '100%', border: 'none', display: 'block', minHeight: 100, height: height + 'px', overflow: 'hidden' }} sandbox="allow-scripts allow-popups" title="email-body" />;
};

const Avatar = ({ name, email: emailAddr, size = 40 }) => {
  const [err, setErr] = useState(false);
  const domain = extractEmailDomain(emailAddr);
  const initials = getInitials(name || emailAddr || '?');

  if (isOwnSenderDomain(domain)) {
    return <img src="/logo.png" alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'contain' }} loading="lazy" />;
  }
  const logo = senderFaviconUrl(domain);
  if (logo && !err) {
    return (
      <div style={{ width: size, height: size, borderRadius: '50%', overflow: 'hidden', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <img src={logo} alt="" style={{ width: size * 0.7, height: size * 0.7, objectFit: 'contain' }} onError={() => setErr(true)} loading="lazy" referrerPolicy="no-referrer" />
      </div>
    );
  }

  const bg = senderAvatarColor(emailAddr);

  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: size * 0.38, flexShrink: 0 }}>
      {initials}
    </div>
  );
};


export const EmailView = ({
  email, folders, onStar, onDelete, onMove, onReply, onReplyAll, onForward,
  onBack, onPrev, onNext, isMobile, currentIndex, totalCount
}) => {
  const [showSenderDetails, setShowSenderDetails] = useState(false);
  const viewRef = useRef(null);
  const touchStart = useRef({ x: 0, y: 0 });

  const handleTouchStart = useCallback((e) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, []);
  const handleTouchEnd = useCallback((e) => {
    const dx = e.changedTouches[0].clientX - touchStart.current.x;
    const dy = Math.abs(e.changedTouches[0].clientY - touchStart.current.y);
    if (dx > 80 && dy < 100 && onBack) onBack();
  }, [onBack]);

  const handleDownloadAttachment = async (att, index) => {
    // Récupérer le token depuis axios (source unique de vérité)
    const authHeader = axios.defaults.headers.common['Authorization'] || '';
    const accountEmail = axios.defaults.headers.common['X-Account-Email'] || '';
    const url = `${API}/emails/attachment?email_id=${encodeURIComponent(email.id)}&att_index=${index}`;

    try {
      const resp = await fetch(url, {
        headers: {
          ...(authHeader ? { 'Authorization': authHeader } : {}),
          ...(accountEmail ? { 'X-Account-Email': accountEmail } : {}),
        }
      });

      if (!resp.ok) {
        const text = await resp.text();
        console.error('Download error:', resp.status, text);
        alert(`Erreur ${resp.status} : impossible de télécharger la pièce jointe`);
        return;
      }

      const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = att.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objUrl), 2000);
    } catch (e) {
      console.error('Download exception:', e);
      alert('Erreur lors du téléchargement');
    }
  };

  if (!email) return null;

  const toolbarBtn = (onClick, icon, title, danger = false) => (
    <button
      onClick={onClick}
      title={title}
      style={{
        width: 36, height: 36, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'none', border: 'none', cursor: 'pointer', color: danger ? '#d93025' : 'hsl(var(--muted-foreground))',
        transition: 'background 0.15s'
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'hsl(var(--accent))'}
      onMouseLeave={e => e.currentTarget.style.background = 'none'}
    >
      {icon}
    </button>
  );

  return (
    <div
      ref={viewRef}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', background: 'hsl(var(--background))' }}
      data-testid="email-view"
    >
      {/* ── TOOLBAR ── */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '4px 8px', borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', flexShrink: 0, gap: 2 }}>
        {/* Back */}
        {toolbarBtn(onBack, <ArrowLeft style={{ width: 20, height: 20 }} />, 'Retour à la liste')}
        <div style={{ width: 1, height: 24, background: 'hsl(var(--border))', margin: '0 4px' }} />
        {/* Actions */}
        {toolbarBtn(() => onMove?.(email.id, folders?.find(f => f.name === 'Archive')?.id), <Archive style={{ width: 18, height: 18 }} />, 'Archiver')}
        {toolbarBtn(() => onMove?.(email.id, folders?.find(f => f.id?.toLowerCase().includes('junk') || f.name?.toLowerCase().includes('spam'))?.id), <AlertOctagon style={{ width: 18, height: 18 }} />, 'Spam')}
        {toolbarBtn(() => onDelete(email.id), <Trash2 style={{ width: 18, height: 18 }} />, 'Supprimer', true)}
        {toolbarBtn(() => onStar(email.id, !email.is_starred), <Star style={{ width: 18, height: 18, fill: email.is_starred ? '#f6ad55' : 'none', color: email.is_starred ? '#f6ad55' : 'hsl(var(--muted-foreground))' }} />, email.is_starred ? 'Retirer des favoris' : 'Ajouter aux favoris')}
        {/* Spacer + nav */}
        <div style={{ flex: 1 }} />
        {(currentIndex != null && totalCount != null) && (
          <span style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))', marginRight: 4 }}>
            {currentIndex + 1} sur {totalCount}
          </span>
        )}
        {toolbarBtn(onPrev, <ChevronLeft style={{ width: 20, height: 20 }} />, 'Précédent', false)}
        {toolbarBtn(onNext, <ChevronRight style={{ width: 20, height: 20 }} />, 'Suivant', false)}
      </div>

      {/* ── CONTENT ── */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '0 12px 20px' }}>
        {/* Subject */}
        <h1 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 400, color: 'hsl(var(--foreground))', margin: '16px 0 12px', lineHeight: 1.3, wordBreak: 'break-word' }}>
          {email.subject || '(Sans objet)'}
          {email.is_starred && <Star style={{ display: 'inline', width: 18, height: 18, marginLeft: 10, fill: '#f6ad55', color: '#f6ad55', verticalAlign: 'middle' }} />}
        </h1>

        {/* Email card */}
        <div style={{ borderRadius: 8, border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', marginBottom: 8, overflow: 'hidden' }}>
          {/* Sender row */}
          <div
            onClick={() => setShowSenderDetails(v => !v)}
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer', borderBottom: showSenderDetails ? '1px solid hsl(var(--border))' : 'none' }}
          >
            <Avatar name={email.from_name} email={primaryFromEmail(email.from_name, email.from_email)} size={40} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'hsl(var(--foreground))', truncate: true }}>
                  {fromDisplayName(email.from_name, email.from_email)}
                </span>
                <span style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))', flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {formatFullDate(email.created_at)}
                </span>
              </div>
              {!showSenderDetails && (
                <span style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>
                  à moi
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
              <button
                onClick={e => { e.stopPropagation(); onStar(email.id, !email.is_starred); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 4 }}
                title={email.is_starred ? 'Retirer' : 'Favori'}
              >
                <Star style={{ width: 16, height: 16, fill: email.is_starred ? '#f6ad55' : 'none', color: email.is_starred ? '#f6ad55' : 'hsl(var(--muted-foreground))' }} />
              </button>
              <button
                onClick={e => { e.stopPropagation(); onReply?.(); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 4, color: 'hsl(var(--muted-foreground))' }}
                title="Répondre"
              >
                <Reply style={{ width: 16, height: 16 }} />
              </button>
              <button
                onClick={e => { e.stopPropagation(); onReplyAll?.(); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 4, color: 'hsl(var(--muted-foreground))' }}
                title="Répondre à tous"
              >
                <ReplyAll style={{ width: 16, height: 16 }} />
              </button>
              <button
                onClick={e => { e.stopPropagation(); onForward?.(); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 4, color: 'hsl(var(--muted-foreground))' }}
                title="Transférer"
              >
                <Forward style={{ width: 16, height: 16 }} />
              </button>
            </div>
          </div>

          {/* Sender details (expanded) */}
          {showSenderDetails && (
            <div style={{ padding: '8px 16px 12px 68px', fontSize: 12, color: 'hsl(var(--muted-foreground))', borderBottom: '1px solid hsl(var(--border))' }}>
              <div><strong>De :</strong> {formatFromDisplay(email.from_name, email.from_email)}</div>
              {email.to_email && <div><strong>À :</strong> {email.to_email}</div>}
              {email.cc && <div><strong>Cc :</strong> {email.cc}</div>}
              <div><strong>Date :</strong> {formatFullDate(email.created_at)}</div>
              <div><strong>Objet :</strong> {email.subject}</div>
            </div>
          )}

          {/* Body — isolé dans iframe pour respecter le fond blanc des emails HTML */}
          <div style={{ background: '#fff', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
            <EmailBodyIframe html={email.body} />
          </div>

          {/* Attachments */}
          {email.attachments?.length > 0 && (
            <div style={{ padding: '12px 16px', borderTop: '1px solid hsl(var(--border))' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>
                <Paperclip style={{ width: 14, height: 14 }} />
                <span>{email.attachments.length} pièce{email.attachments.length > 1 ? 's' : ''} jointe{email.attachments.length > 1 ? 's' : ''}</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {email.attachments.map((att) => (
                  <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, border: '1px solid hsl(var(--border))', background: 'hsl(var(--muted))', maxWidth: 240 }}>
                    <Paperclip style={{ width: 14, height: 14, flexShrink: 0, color: 'hsl(var(--muted-foreground))' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'hsl(var(--foreground))' }}>{att.filename}</p>
                      <p style={{ fontSize: 11, color: 'hsl(var(--muted-foreground))' }}>{formatFileSize(att.size)}</p>
                    </div>
                    <button
                      onClick={() => handleDownloadAttachment(att, email.attachments.indexOf(att))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'hsl(var(--muted-foreground))' }}
                      title="Télécharger"
                    >
                      <Download style={{ width: 14, height: 14 }} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Reply / Forward buttons (Gmail style) — ouvrent le ComposeModal complet */}
        <div style={{ display: 'flex', gap: 8, padding: '8px 0 20px' }}>
          <button
            onClick={() => onReply?.()}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 20px', borderRadius: 20, border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', cursor: 'pointer', fontSize: 14, color: 'hsl(var(--foreground))', fontWeight: 500 }}
            onMouseEnter={e => e.currentTarget.style.background = 'hsl(var(--accent))'}
            onMouseLeave={e => e.currentTarget.style.background = 'hsl(var(--card))'}
          >
            <Reply style={{ width: 16, height: 16 }} />
            Répondre
          </button>
          <button
            onClick={() => onReplyAll?.()}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 20px', borderRadius: 20, border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', cursor: 'pointer', fontSize: 14, color: 'hsl(var(--foreground))', fontWeight: 500 }}
            onMouseEnter={e => e.currentTarget.style.background = 'hsl(var(--accent))'}
            onMouseLeave={e => e.currentTarget.style.background = 'hsl(var(--card))'}
          >
            <ReplyAll style={{ width: 16, height: 16 }} />
            Répondre à tous
          </button>
          <button
            onClick={() => onForward?.()}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 20px', borderRadius: 20, border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', cursor: 'pointer', fontSize: 14, color: 'hsl(var(--foreground))', fontWeight: 500 }}
            onMouseEnter={e => e.currentTarget.style.background = 'hsl(var(--accent))'}
            onMouseLeave={e => e.currentTarget.style.background = 'hsl(var(--card))'}
          >
            <Forward style={{ width: 16, height: 16 }} />
            Transférer
          </button>
        </div>
      </div>
    </div>
  );
};
